/**
 * Converte o resultado do questionário guiado no MESMO formato que o
 * excelProcessor produz ao importar planilha. Os dashboards, o motor de
 * insights e o PDF consomem esse formato sem saber de onde veio o dado.
 *
 * Regra: qualquer mudança aqui precisa espelhar
 * `dashboard/backend/services/excelProcessor.js` (extractReceitas /
 * extractDespesas / buildIndicatorsFromInput).
 */

import { GRUPOS, camposDeMes, rotulosMeses, MESES_PADRAO } from '../config/planoContas';

/**
 * Numero digitado (ou COLADO) no questionario.
 *
 * `Number('R$ 1.234,56')` e `NaN`, e `NaN` caia para 0 aqui: quem colava um
 * valor formatado de outra planilha via o campo aceitar e o mes sair zerado.
 * Espelha `backend/services/valorBrasileiro.paraNumero` -- mexeu num, mexa no
 * outro; o teste `manualEntryTransform.test.js` tranca os casos.
 */
const num = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;

  let texto = String(v).trim();
  if (!texto) return 0;

  let negativo = false;
  if (/^\(.*\)$/.test(texto)) { negativo = true; texto = texto.slice(1, -1).trim(); }
  if (/-\s*$/.test(texto)) { negativo = true; texto = texto.replace(/-\s*$/, '').trim(); }
  if (/^-/.test(texto)) { negativo = true; texto = texto.replace(/^-\s*/, '').trim(); }

  texto = texto.replace(/%/g, '').replace(/(r\$|\$)/gi, '').replace(/\s| /g, '');
  if (!/^[\d.,]+$/.test(texto)) return 0;

  const ultimaVirgula = texto.lastIndexOf(',');
  const ultimoPonto = texto.lastIndexOf('.');
  let normalizado = texto;
  if (ultimaVirgula >= 0 && ultimoPonto >= 0) {
    const decimal = Math.max(ultimaVirgula, ultimoPonto);
    const milhar = decimal === ultimaVirgula ? /\./g : /,/g;
    normalizado = `${texto.slice(0, decimal).replace(milhar, '')}.${texto.slice(decimal + 1)}`;
  } else if (ultimaVirgula >= 0) {
    const partes = texto.split(',');
    normalizado = (partes.length > 2 || (partes[1] || '').length === 3 && partes[0].length <= 3)
      ? texto.replace(/,/g, '')
      : `${partes[0]}.${partes.slice(1).join('')}`;
  } else if (ultimoPonto >= 0) {
    const partes = texto.split('.');
    normalizado = (partes.length > 2 || ((partes[1] || '').length === 3 && partes[0].length <= 3))
      ? texto.replace(/\./g, '')
      : texto;
  }

  const n = Number(normalizado);
  if (!Number.isFinite(n)) return 0;
  return negativo ? -n : n;
};

const gruposPorCategoria = (categoria) => GRUPOS.filter((g) => g.categoria === categoria);

const contasDaCategoria = (grupos, categoria) =>
  gruposPorCategoria(categoria).flatMap((g) => grupos[g.id] || []);

/** Variação percentual mês a mês; o primeiro mês não tem anterior. */
const crescimentoMesAMes = (valores) =>
  valores.map((valor, i) => {
    if (i === 0) return 0;
    const anterior = valores[i - 1];
    return anterior !== 0 ? ((valor - anterior) / anterior) * 100 : 0;
  });

/**
 * Quantos meses este preenchimento cobre.
 *
 * A fonte de verdade é o próprio dado: o wizard entrega `meses` com os rótulos
 * reais da janela escolhida (3, 6 ou 12). A configuração é o segundo recurso, e
 * o padrão do plano de contas o terceiro — que é o que faz um `formData`
 * gravado antes de a escolha de janela existir continuar lendo três meses.
 */
const quantidadeDeMeses = (formData) => {
  const pelosRotulos = Array.isArray(formData?.meses) ? formData.meses.length : 0;
  if (pelosRotulos > 0) return pelosRotulos;
  const daConfig = Number(formData?.config?.quantidadeMeses);
  return daConfig > 0 ? Math.floor(daConfig) : MESES_PADRAO;
};

export function construirDRE(formData) {
  const { config, grupos } = formData;

  // A janela inteira sai daqui, e os ajudantes abaixo são FECHAMENTOS sobre
  // ela. Uma tentativa anterior passou a lista de campos como parâmetro de
  // ajudantes de módulo e quebrou com `campos.some is not a function`, porque
  // um dos treze pontos de chamada esqueceu o argumento. Sem parâmetro não
  // existe ponto de chamada que possa errar.
  const meses = quantidadeDeMeses(formData);
  const campos = camposDeMes(meses);

  /** Uma posição por mês da janela. */
  const porMes = (calcular) => Array.from({ length: meses }, (_, i) => calcular(i));

  /** Soma uma lista de contas mês a mês -> [mes1, ..., mesN] */
  const somarPorMes = (contas) =>
    campos.map((campo) => (contas || []).reduce((s, c) => s + num(c[campo]), 0));

  /** Vira { 'Nome da conta': { mes1, ..., mesN } }, ignorando conta sem nome ou zerada */
  /**
   * Contas de um grupo, indexadas pelo CÓDIGO.
   *
   * A chave era o nome, e duas contas homônimas — o aluguel de duas lojas, o
   * salário de duas filiais — colidiam: a última apagava a primeira e a tabela
   * do DRE exibia uma linha a menos, enquanto o total continuava certo. Chave é
   * identidade; `nome` é rótulo e passa a viajar dentro do valor, exatamente
   * como a importação de planilha faz (regra de paridade dos dois caminhos).
   */
  const mapearContas = (contas) => {
    const saida = {};
    (contas || []).forEach((conta) => {
      const nome = (conta.nome || '').trim();
      if (!nome) return;
      const codigo = String(conta.codigo || '').trim();
      const valores = Object.fromEntries(campos.map((campo) => [campo, num(conta[campo])]));
      const temValor = campos.some((campo) => valores[campo] !== 0);
      if (!temValor) return;
      saida[codigo || nome] = { ...valores, nome, ...(codigo ? { codigo } : {}) };
    });
    return saida;
  };

  /** Bucket de custo fixo já no formato { conta: {...}, total: [] } */
  const bucket = (contas) => ({ ...mapearContas(contas), total: somarPorMes(contas) });

  const somarArrays = (...arrays) =>
    porMes((i) => arrays.reduce((s, arr) => s + (arr[i] || 0), 0));

  /**
   * "Disponível" = o usuário realmente informou algo, ainda que zero digitado.
   * Campo deixado em branco não é zero: o dashboard precisa saber a diferença
   * para dizer "não informado" em vez de exibir R$ 0,00 como se fosse medição.
   */
  const temInformacao = (contas) =>
    (contas || []).some((conta) =>
      campos.some((campo) => String(conta[campo] ?? '').trim() !== '')
    );

  const receitas = contasDaCategoria(grupos, 'receita');
  const receitasNaoOp = contasDaCategoria(grupos, 'receitaNaoOperacional');
  const variaveis = contasDaCategoria(grupos, 'variavel');

  const pessoal = contasDaCategoria(grupos, 'pessoal');
  const socios = contasDaCategoria(grupos, 'socios');
  const administrativas = contasDaCategoria(grupos, 'administrativa');
  const financeiras = contasDaCategoria(grupos, 'financeira');
  const marketing = contasDaCategoria(grupos, 'marketing');

  const totalReceitas = somarPorMes(receitas);
  const totalVariaveis = somarPorMes(variaveis);

  // O grupo de custo variável do questionário carrega o código contábil de cada
  // conta, e cada pedaço entra numa linha diferente do DRE:
  //   3.2.2   mercadoria / matéria-prima -> CMV
  //   3.2.3.2 imposto sobre venda        -> dedução de receita
  //   o resto (comissão, taxa de cartão, conta acrescentada pelo usuário)
  //           -> custo variável de venda
  // Sem essa separação, margem bruta e margem de contribuição viram a mesma
  // conta e toda margem divide por receita bruta.
  const porCodigo = (prefixo) =>
    somarPorMes(variaveis.filter((c) => String(c.codigo || '').startsWith(prefixo)));

  const cmv = porCodigo('3.2.2');
  const deducoes = porCodigo('3.2.3.2');
  const receitaLiquida = porMes((i) => totalReceitas[i] - deducoes[i]);
  const custosVariaveisDeVenda = porMes((i) => totalVariaveis[i] - cmv[i] - deducoes[i]);

  const totalPessoal = somarPorMes(pessoal);
  const totalSocios = somarPorMes(socios);
  const totalAdm = somarPorMes(administrativas);
  const totalFin = somarPorMes(financeiras);
  const totalMkt = somarPorMes(marketing);

  const totalCustosFixos = somarArrays(totalPessoal, totalSocios, totalAdm, totalFin, totalMkt);

  // Juros são despesa; parcela de empréstimo é amortização do principal. Somar
  // as duas inflava o denominador do ICR e do DSCR. Na planilha quem separa é a
  // coluna "Custo?"; aqui, que não tem essa coluna, a separação é pelo código —
  // e os dois códigos excluídos são exatamente os que a planilha marca "Não".
  const AMORTIZACAO = ['2.1.1.4.1', '2.1.1.4.3'];
  const ehAmortizacao = (conta) => AMORTIZACAO.includes(String(conta.codigo || ''));
  const juros = somarPorMes(financeiras.filter((c) => !ehAmortizacao(c)));
  const amortizacaoDivida = somarPorMes(financeiras.filter(ehAmortizacao));

  const lucroReal = porMes((i) => totalReceitas[i] - totalVariaveis[i] - totalCustosFixos[i]);

  // Ponto de equilíbrio: custo fixo dividido pela margem de contribuição
  // percentual. Só existe com margem positiva — senão nenhum faturamento
  // cobre os custos e o número não significa nada.
  let margemPositiva = false;
  const pontoEquilibrio = porMes((i) => {
    const receita = totalReceitas[i];
    if (receita <= 0) return 0;
    const margemPercentual = (receita - totalVariaveis[i]) / receita;
    if (margemPercentual <= 0) return 0;
    margemPositiva = true;
    return totalCustosFixos[i] / margemPercentual;
  });

  const receitaInformada = temInformacao(receitas);
  const variaveisInformados = temInformacao(variaveis);
  const fixosInformados = [pessoal, socios, administrativas, financeiras, marketing].some(
    temInformacao
  );

  // Espelha excelProcessor.buildIndicatorsFromInput: sem esse metadado o
  // backend trata todo indicador como "sem dado de origem" e devolve null.
  const availability = {
    faturamento: receitaInformada,
    custosFixos: fixosInformados,
    custosVariaveis: variaveisInformados,
    lucroReal: receitaInformada && (variaveisInformados || fixosInformados),
    lucroCompetencia: receitaInformada && (variaveisInformados || fixosInformados),
    receitaLiquida: receitaInformada,
    // No questionário a mercadoria é perguntada como custo do mês, então o
    // valor informado JÁ é o CMV — não existe compra de estoque separada como
    // na planilha, e por isso `compraMercadoria` fica indisponível aqui.
    cmv: variaveisInformados,
    custosVariaveisDeVenda: variaveisInformados,
    custosComercializacao: variaveisInformados,
    custoTotalReal: variaveisInformados || fixosInformados,
    despesasFinanceiras: temInformacao(financeiras),
    amortizacaoDivida: temInformacao(financeiras),
    compraMercadoria: false,
    custosInvisiveis: false,
    cmvPercentual: false,
    // O questionário não coleta lucro percebido (regime de caixa da planilha).
    lucroPercebido: false,
    pontoEquilibrio: receitaInformada && fixosInformados && variaveisInformados && margemPositiva
  };

  return {
    empresa: (config.empresa || '').trim() || 'Minha Empresa',
    periodo: { mes: config.mes, ano: config.ano },
    // Os meses reais que foram perguntados. É o que permite ao dashboard
    // rotular os gráficos com "Jun/26" em vez de "Mês 1". A janela entra aqui
    // com a MESMA quantidade que gerou as séries acima: rótulo em quantidade
    // diferente do dado desalinha o gráfico sem que nada acuse — a tela mostra
    // números certos com meses errados.
    periodos: (formData.meses || rotulosMeses(config.mes, config.ano, meses)).map((m) => ({
      mes: m.mes,
      ano: m.ano,
      curto: m.curto,
      longo: m.longo
    })),
    origem: 'manual',
    receitas: {
      operacionais: mapearContas(receitas),
      naoOperacionais: mapearContas(receitasNaoOp),
      total: totalReceitas
    },
    despesas: {
      custosVariaveis: { ...mapearContas(variaveis), total: totalVariaveis },
      // Os cinco buckets existem sempre: DRETab acessa custosFixos.socios.total direto.
      custosFixos: {
        pessoal: bucket(pessoal),
        socios: bucket(socios),
        administrativas: bucket(administrativas),
        financeiras: bucket(financeiras),
        marketing: bucket(marketing),
        total: totalCustosFixos
      }
    },
    indicadores: {
      faturamento: {
        valores: totalReceitas,
        crescimento: crescimentoMesAMes(totalReceitas),
        acumulado: totalReceitas.reduce((soma, valor) => soma + valor, 0)
      },
      custosFixos: { valores: totalCustosFixos },
      custosVariaveis: { valores: totalVariaveis },
      receitaLiquida: { valores: receitaLiquida },
      deducoes: { valores: deducoes },
      cmv: { valores: cmv },
      custosVariaveisDeVenda: { valores: custosVariaveisDeVenda },
      // Tudo que a venda custa além da mercadoria: imposto sobre venda, comissão
      // e taxa de cartão. É o bloco 3.2.3 da planilha.
      custosComercializacao: {
        valores: porMes((i) => deducoes[i] + custosVariaveisDeVenda[i])
      },
      despesasFinanceiras: { valores: juros },
      amortizacaoDivida: { valores: amortizacaoDivida },
      custoTotalReal: {
        valores: porMes((i) => totalVariaveis[i] + totalCustosFixos[i])
      },
      lucro: {
        real: { valores: lucroReal },
        percebido: { valores: porMes(() => 0) },
        // No questionário a mercadoria já é perguntada como custo do mês, então
        // o resultado informado JÁ é de competência. Na planilha as duas leituras
        // diferem — lá o "Lucro Real" é de caixa e o CMV entra depois.
        competencia: { valores: lucroReal }
      },
      pontoEquilibrio: { valores: availability.pontoEquilibrio ? pontoEquilibrio : porMes(() => 0) },
      _availability: availability
    }
  };
}

/**
 * Balanço é opcional. Quando pulado devolve null — o backend e o dashboard
 * já tratam ausência de balanço marcando os indicadores dependentes como
 * dado faltante, em vez de exibir zero como se fosse medição.
 */
export function construirBalanco(formData) {
  const b = formData.balanco || {};
  if (b.preenchido === false) return null;

  const ativo = b.ativo || {};
  const passivo = b.passivo || {};
  const patrimonio = b.patrimonio || {};

  const ativoCirculante = num(ativo.disponivel) + num(ativo.contasReceber) + num(ativo.estoque);
  const ativoNaoCirculante = num(ativo.imobilizado) + num(ativo.investimentos);
  const ativoTotal = ativoCirculante + ativoNaoCirculante;

  const passivoCirculante =
    num(passivo.fornecedores) + num(passivo.emprestimos) + num(passivo.impostos);
  const passivoNaoCirculante = num(passivo.emprestimosLP);

  // Sem PL informado, fecha a equação patrimonial: PL = Ativo - Passivo
  const patrimonioLiquido =
    num(patrimonio.total) || ativoTotal - (passivoCirculante + passivoNaoCirculante);

  // Nenhum campo preenchido = tratar como não informado
  if (ativoTotal === 0 && passivoCirculante === 0 && passivoNaoCirculante === 0) return null;

  return {
    origem: 'manual',
    ativo: {
      circulante: {
        caixa: num(ativo.disponivel),
        // O questionário pergunta "disponível", que já soma caixa e bancos. Zerar
        // as duas linhas seguintes afirmaria saldo zero em banco para quem nunca
        // foi perguntado — e ausência não é zero em lugar nenhum deste produto.
        bancos: null,
        aplicacoes: null,
        contasAReceber: num(ativo.contasReceber),
        estoque: num(ativo.estoque),
        total: ativoCirculante
      },
      naoCirculante: {
        imobilizado: num(ativo.imobilizado),
        investimentos: num(ativo.investimentos),
        total: ativoNaoCirculante
      },
      total: ativoTotal
    },
    passivo: {
      circulante: {
        fornecedores: num(passivo.fornecedores),
        financiamentos: num(passivo.emprestimos),
        impostos: num(passivo.impostos),
        total: passivoCirculante
      },
      naoCirculante: {
        financiamentosLP: passivoNaoCirculante,
        total: passivoNaoCirculante
      },
      patrimonioLiquido: { total: patrimonioLiquido },
      total: passivoCirculante + passivoNaoCirculante + patrimonioLiquido
    },
    // `null` quando o denominador não existe, NUNCA zero.
    //
    // Zero aqui era lido adiante como medição: o pilar de endividamento do
    // Índice de Saúde entra com `typeof valor === 'number'`, e endividamento
    // zero vale nota máxima. Uma empresa sem passivo informado saía "sem
    // dívida", que é afirmação que ninguém fez. Espelha a mesma regra do
    // `buscarIndicador` da importação de planilha.
    indicadores: {
      liquidezCorrente: passivoCirculante > 0 ? ativoCirculante / passivoCirculante : null,
      endividamento:
        ativoTotal > 0 ? (passivoCirculante + passivoNaoCirculante) / ativoTotal : null
    }
  };
}
